import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Token kept in localStorage when "remember me" is checked (persists across browser
// sessions), or sessionStorage otherwise (cleared when the tab closes).
const getStoredToken = () =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

const storeToken = (token, remember) => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  if (remember) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }
};

const clearStoredToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Global logout function - clears everything (no redirect)
  const forceLogout = useCallback(() => {
    clearStoredToken();
    setUser(null);
  }, []);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = getStoredToken();
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch (err) {
          // Only force-logout when the server explicitly rejected the token.
          // Transient errors (network blip, 5xx, request aborted by a refresh)
          // should leave the stored token alone so the next attempt re-auths.
          if (err.response?.status === 401) {
            forceLogout();
          }
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, [forceLogout]);

  // Listen for storage changes (logout in other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        forceLogout();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [forceLogout]);

  // Register
  const register = async (data) => {
    setError(null);
    try {
      const response = await api.post('/auth/register', data);
      storeToken(response.data.token, true);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'שגיאה בהרשמה';
      setError(message);
      throw new Error(message);
    }
  };

  // Login
  const login = async (email, password, rememberMe = true) => {
    setError(null);
    try {
      const response = await api.post('/auth/login', { email, password });
      storeToken(response.data.token, rememberMe);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'שגיאה בהתחברות';
      setError(message);
      throw new Error(message);
    }
  };

  // Logout - clears session but leaves the remembered email so the next login
  // can pre-fill it. Email is only forgotten when the user logs in with "remember
  // me" unchecked.
  const logout = () => {
    clearStoredToken();
    setUser(null);
  };

  // Update registration step
  const updateRegistrationStep = async (step, data) => {
    try {
      const response = await api.put('/auth/registration-step', { step, data });
      setUser(prev => ({
        ...prev,
        registrationStep: response.data.user.registrationStep,
        registrationComplete: response.data.user.registrationComplete
      }));
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'שגיאה בעדכון הפרטים';
      throw new Error(message);
    }
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      const response = await api.put('/users/profile', data);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'שגיאה בעדכון הפרופיל';
      throw new Error(message);
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'שגיאה בשינוי הסיסמה';
      throw new Error(message);
    }
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      return response.data.user;
    } catch (err) {
      console.error('Error refreshing user:', err);
      return null;
    }
  };

  const value = {
    user,
    isLoading,
    error,
    register,
    login,
    logout,
    forceLogout,
    updateRegistrationStep,
    updateProfile,
    changePassword,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
